import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import axios from 'axios'
import AgentPanel from '../components/AgentPanel'
import ResultsPanel from '../components/ResultsPanel'
import PropertyInput from '../components/PropertyInput'
import ErrorBoundary from '../components/ErrorBoundary'
import DealPackTab from '../components/DealPackTab'
import { useAgentChat } from '../hooks/useAgentChat'
import { MONTE_CARLO_STRATEGY_KEYS } from '../config/strategyConfig'
import { safeParseAnalysisResult } from '../schemas/analysisResult.zod'

/** UK EPC asset rating band A–G (uppercase), or null if missing / not a single valid band. */
function normalizeEpcBand(rating) {
  if (rating == null || rating === '') return null
  const s = String(rating).trim().toUpperCase()
  return /^[A-G]$/.test(s) ? s : null
}

/** Display: normalized A–G if possible, else raw listing text, else em dash. */
function formatListedEpc(rating) {
  const band = normalizeEpcBand(rating)
  if (band) return band
  if (rating != null && String(rating).trim() !== '') return String(rating).trim()
  return '—'
}

function AnalysisDashboard({ propertyKey, detailsUnavailable, onBack, onOpenLandlordReport }) {
  const [property, setProperty] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [agents, setAgents] = useState({
    research: { status: 'pending', progress: 0, logs: [] },
    valuation: { status: 'pending', progress: 0, logs: [] },
    risk: { status: 'pending', progress: 0, logs: [] },
    compliance: { status: 'pending', progress: 0, logs: [] },
    underwriting: { status: 'pending', progress: 0, logs: [] }
  })
  const [result, setResult] = useState(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [mcRealismByStrategy, setMcRealismByStrategy] = useState({})
  /** L0–L2 applied to all MC strategy keys on the first WebSocket run (and Re-Analyze until changed). */
  const [preRunRealismLevel, setPreRunRealismLevel] = useState(1)
  /** Active tab in the right-panel area (shown after analysis completes). */
  const [activeTab, setActiveTab] = useState('results')
  /** Calibration metadata fetched from /api/v1/config on mount. */
  const [calibration, setCalibration] = useState(null)
  /** STL advisory enabled flag (from /api/v1/config). */
  const [stlAdvisoryEnabled, setStlAdvisoryEnabled] = useState(false)
  /** Whether user has opted to include STL in this analysis (shown only when stlAdvisoryEnabled). */
  const [stlIncluded, setStlIncluded] = useState(false)

  const wsRef = useRef(null)
  const timerRef = useRef(null)
  const mcRealismRef = useRef(mcRealismByStrategy)
  mcRealismRef.current = mcRealismByStrategy

  /** After analysis, backend enriches rent/yields on property_snapshot; left panel must show that, not stale GET /properties. */
  const propertyForPanel = useMemo(() => {
    if (!property) return null
    const snap = result?.property_snapshot
    if (snap && typeof snap === 'object') return snap
    return property
  }, [property, result])

  // Prepare agent logs for chat context
  const agentLogs = useMemo(() => {
    const logs = {}
    Object.entries(agents).forEach(([key, agent]) => {
      logs[key] = agent.logs || []
    })
    return logs
  }, [agents])

  // Initialize chat hook
  const {
    messages: chatMessages,
    isConnected: isChatConnected,
    isConnecting: isChatConnecting,
    isStreaming: isChatStreaming,
    error: chatError,
    sendMessage: sendChatMessage,
    reconnect: reconnectChat,
    clearMessages: clearChatMessages
  } = useAgentChat(
    propertyKey,
    result,
    agentLogs
  )

  useEffect(() => {
    fetchProperty()
    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [propertyKey])

  // Fetch calibration metadata once on mount
  useEffect(() => {
    const API_BASE = import.meta.env.VITE_API_URL
      ? `${String(import.meta.env.VITE_API_URL).replace(/\/$/, '')}/api/v1`
      : '/api/v1'
    fetch(`${API_BASE}/config`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.calibration) setCalibration(data.calibration)
        if (data?.stl_advisory) setStlAdvisoryEnabled(true)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!result?.strategy_analysis?.monte_carlo?.by_strategy) return
    const next = {}
    for (const row of result.strategy_analysis.monte_carlo.by_strategy) {
      next[row.strategy] = row.realism_level
    }
    setMcRealismByStrategy(next)
  }, [result?.id])

  const mergeMonteCarloBundle = useCallback((bundle) => {
    if (!bundle?.by_strategy?.length) return
    setResult((prev) => {
      if (!prev?.strategy_analysis) return prev
      const prevRows = prev.strategy_analysis.monte_carlo?.by_strategy || []
      const map = Object.fromEntries(prevRows.map((r) => [r.strategy, r]))
      for (const row of bundle.by_strategy) {
        map[row.strategy] = row
      }
      return {
        ...prev,
        strategy_analysis: {
          ...prev.strategy_analysis,
          monte_carlo: { by_strategy: Object.values(map) },
        },
      }
    })
  }, [])

  const recomputeMonteCarlo = useCallback(
    async (strategiesPayload) => {
      if (!result?.id) return
      const API_BASE = import.meta.env.VITE_API_URL || ''
      const { data } = await axios.post(
        `${API_BASE}/api/v1/monte-carlo/run`,
        {
          analysis_id: result.id,
          strategies: strategiesPayload,
        }
      )
      mergeMonteCarloBundle(data)
    },
    [result?.id, mergeMonteCarloBundle]
  )

  const fetchProperty = async () => {
    try {
      setLoading(true)
      setError(null)
      setResult(null)
      setMcRealismByStrategy({})
      setPreRunRealismLevel(1)

      // Fetch property by ID (from search selection or manual entry)
      const API_BASE = import.meta.env.VITE_API_URL || ''
      const response = await axios.get(`${API_BASE}/api/v1/properties/${propertyKey}`)
      const propertyData = response.data
      
      if (propertyData) {
        setProperty(propertyData)
      } else {
        throw new Error('Property not found')
      }
    } catch (error) {
      console.error('Failed to fetch property:', error)
      setError(error.response?.data?.detail || error.message || 'Failed to load property')
    } finally {
      setLoading(false)
    }
  }

  const buildMcMapForLevel = useCallback((level) => {
    const n = Math.max(0, Math.min(2, Number(level) || 0))
    return Object.fromEntries([...MONTE_CARLO_STRATEGY_KEYS].map((k) => [k, n]))
  }, [])

  const handleWebSocketMessage = useCallback((message) => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/0f50a1bb-06d4-4480-a68c-70f1e59a674f', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        location: 'AnalysisDashboard.jsx:handleWebSocketMessage',
        message: 'ws_message',
        data: {
          type: message?.type,
          hasResult: message?.result != null,
          resultId: message?.result?.id,
          resultKeys: message?.result && typeof message.result === 'object' ? Object.keys(message.result).slice(0, 12) : [],
        },
        timestamp: Date.now(),
        hypothesisId: 'H1_ws_payload',
      }),
    }).catch(() => {})
    // #endregion
    if (message.type === 'agent_update') {
      setAgents((prev) => ({
        ...prev,
        [message.agent_name]: {
          status: message.status,
          progress: message.progress,
          logs: [...(prev[message.agent_name]?.logs || []), message.log_message].slice(-5),
        },
      }))
    } else if (message.type === 'analysis_complete') {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/0f50a1bb-06d4-4480-a68c-70f1e59a674f', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'AnalysisDashboard.jsx:analysis_complete',
          message: 'applying_setResult',
          data: { resultId: message?.result?.id, typeofResult: typeof message?.result },
          timestamp: Date.now(),
          hypothesisId: 'H2_setResult',
        }),
      }).catch(() => {})
      // #endregion
      const checked = safeParseAnalysisResult(message.result)
      if (!checked.success) {
        console.warn('AnalysisResult payload outside expected schema:', checked.error.flatten())
      }
      setResult(message.result)
      setIsAnalyzing(false)
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    } else if (message.type === 'error') {
      console.error('Analysis error:', message.message)
      setError(message.message)
      setIsAnalyzing(false)
    }
  }, [])

  const startAnalysis = useCallback((prop = property, options = {}) => {
    const { mcLevelsOverride } = options
    if (!prop) return

    if (wsRef.current) {
      try {
        wsRef.current.close()
      } catch {
        /* ignore */
      }
      wsRef.current = null
    }

    setError(null)

    const mcMap =
      mcLevelsOverride != null && typeof mcLevelsOverride === 'object'
        ? mcLevelsOverride
        : mcRealismRef.current || {}

    mcRealismRef.current = mcMap
    if (mcLevelsOverride != null) {
      setMcRealismByStrategy(mcLevelsOverride)
    }

    setIsAnalyzing(true)
    setElapsedTime(0)
    setResult(null)
    clearChatMessages() // Clear chat when starting new analysis
    
    // Reset agents
    setAgents({
      research: { status: 'pending', progress: 0, logs: [] },
      valuation: { status: 'pending', progress: 0, logs: [] },
      risk: { status: 'pending', progress: 0, logs: [] },
      compliance: { status: 'pending', progress: 0, logs: [] },
      underwriting: { status: 'pending', progress: 0, logs: [] }
    })
    
    // Start timer
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 0.1)
    }, 100)

    // Connect WebSocket - use property ID
    const propertyId = prop.id || propertyKey
    const WS_BASE = import.meta.env.VITE_WS_URL || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`
    const mcLive = mcRealismRef.current || {}
    const mcParam =
      Object.keys(mcLive).length > 0
        ? `?mc=${encodeURIComponent(JSON.stringify(mcLive))}`
        : ''
    const wsUrl = `${WS_BASE}/api/v1/ws/analyze/${propertyId}${mcParam}`
    
    console.log('Connecting to WebSocket:', wsUrl)
    wsRef.current = new WebSocket(wsUrl)

    wsRef.current.onopen = () => {
      console.log('WebSocket connected')
    }

    wsRef.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)
        handleWebSocketMessage(message)
      } catch (e) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/0f50a1bb-06d4-4480-a68c-70f1e59a674f', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: 'AnalysisDashboard.jsx:onmessage',
            message: 'json_parse_failed',
            data: { err: String(e?.message || e) },
            timestamp: Date.now(),
            hypothesisId: 'H3_parse',
          }),
        }).catch(() => {})
        // #endregion
        throw e
      }
    }

    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error)
      setError('Connection error. Please try again.')
      setIsAnalyzing(false)
    }

    wsRef.current.onclose = () => {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/0f50a1bb-06d4-4480-a68c-70f1e59a674f', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'AnalysisDashboard.jsx:onclose',
          message: 'ws_closed',
          data: {},
          timestamp: Date.now(),
          hypothesisId: 'H4_close_race',
        }),
      }).catch(() => {})
      // #endregion
      setIsAnalyzing(false)
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [property, propertyKey, clearChatMessages, handleWebSocketMessage])

  const handleStartAnalysis = useCallback(() => {
    if (!property) return
    const map = buildMcMapForLevel(preRunRealismLevel)
    startAnalysis(property, { mcLevelsOverride: map })
  }, [property, preRunRealismLevel, buildMcMapForLevel, startAnalysis])

  const resetAnalysis = () => {
    if (wsRef.current) {
      try {
        wsRef.current.close()
      } catch {
        /* ignore */
      }
      wsRef.current = null
    }
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    setIsAnalyzing(false)
    setAgents({
      research: { status: 'pending', progress: 0, logs: [] },
      valuation: { status: 'pending', progress: 0, logs: [] },
      risk: { status: 'pending', progress: 0, logs: [] },
      compliance: { status: 'pending', progress: 0, logs: [] },
      underwriting: { status: 'pending', progress: 0, logs: [] }
    })
    setResult(null)
    setElapsedTime(0)
    setError(null)
    clearChatMessages()
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading property...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error && !property) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Failed to Load Property</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={onBack}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            ← Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-navy-900 text-white py-4 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="text-gray-300 hover:text-white">
              ← Back
            </button>
            <h1 className="text-xl font-bold">DealPack Pro Analysis</h1>
          </div>
          
          <div className="flex items-center gap-6">
            {isAnalyzing && (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span className="text-sm">Analyzing...</span>
                <span className="text-sm text-gray-400">{elapsedTime.toFixed(1)}s</span>
              </div>
            )}
            {result && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-400">
                  Completed in{' '}
                  {(() => {
                    const sec = Number(result.analysis_duration_seconds);
                    if (Number.isFinite(sec)) return `${sec.toFixed(1)}s`;
                    return `${elapsedTime.toFixed(1)}s`;
                  })()}
                </span>
                {onOpenLandlordReport && (
                  <button
                    onClick={() => onOpenLandlordReport(result, false)}
                    className="bg-emerald-700 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm transition-colors flex items-center gap-1.5"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Landlord Report
                  </button>
                )}
                <button onClick={resetAnalysis} className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm transition-colors">
                  Re-Analyze
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {calibration?.is_prior && (
        <div className="mx-6 mt-4 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
          <svg className="h-4 w-4 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
          </svg>
          <p className="text-xs text-blue-700">
            <span className="font-semibold">Calibration: prior estimates ({calibration.version})</span>
            {' — '}
            {calibration.note}
          </p>
        </div>
      )}

      {detailsUnavailable && (
        <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" role="alert">
          <span className="text-amber-600 text-xl" aria-hidden>⚠️</span>
          <div>
            <p className="font-medium text-amber-800">Detailed property data was not available</p>
            <p className="text-sm text-amber-700 mt-0.5">
              Analysis is based on the listing summary only. Some figures (e.g. sq ft, tenure, EPC) may be missing or estimated.
            </p>
          </div>
        </div>
      )}

      {property && !result && !isAnalyzing && (
        <div className="mx-6 mt-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" role="region" aria-label="Monte Carlo realism before analysis">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-bold text-gray-900">Monte Carlo realism (first run)</h2>
            <p className="text-xs text-gray-500 mt-1">
              L0 = fast / coarse · L1 = financing and transaction costs · L2 = tax and regime approximations (illustrative). This applies to all modelled strategies; you can change per strategy after results load.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <span className="whitespace-nowrap">Level</span>
              <select
                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white"
                value={preRunRealismLevel}
                onChange={(e) => setPreRunRealismLevel(Number(e.target.value))}
                aria-label="Monte Carlo realism level for first analysis"
              >
                <option value={0}>L0</option>
                <option value={1}>L1</option>
                <option value={2}>L2</option>
              </select>
            </label>
            <button
              type="button"
              onClick={handleStartAnalysis}
              className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-semibold transition-colors"
            >
              Start analysis
            </button>
          </div>
        </div>
      )}

      {/* Main Content - 3 Panel Layout */}
      <div className="flex-1 p-4 overflow-hidden">
        <div className="h-full grid grid-cols-12 gap-4">
          
          {/* Left Panel: Property Input with Chat */}
          <div className="col-span-12 lg:col-span-3 h-full hidden lg:block">
            <PropertyInput 
              property={propertyForPanel}
              // Chat props
              chatMessages={chatMessages}
              isChatConnected={isChatConnected}
              isChatConnecting={isChatConnecting}
              isChatStreaming={isChatStreaming}
              chatError={chatError}
              onSendChatMessage={sendChatMessage}
              onReconnectChat={reconnectChat}
              analysisResult={result}
              isAnalysisComplete={!!result && !isAnalyzing}
            />
          </div>

          {/* Center Panel: AI Agents */}
          <div className="col-span-12 md:col-span-5 lg:col-span-4 h-full">
            <AgentPanel agents={agents} />
          </div>

          {/* Right Panel: Tabbed results + AC panels */}
          <div className="col-span-12 md:col-span-7 lg:col-span-5 h-full overflow-hidden flex flex-col">
            {/* Tab bar — only shown when analysis is complete */}
            {result && (
              <div className="flex border-b border-gray-200 mb-2 flex-shrink-0 overflow-x-auto">
                {[
                  { id: 'results', label: 'Results' },
                  { id: 'cost-stack', label: 'Cost Stack' },
                  { id: 'regime', label: 'Regulatory Regime' },
                  { id: 'deal-settings', label: 'Deal Settings' },
                  { id: 'deal-pack', label: 'Deal Pack' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary-600 text-primary-700 bg-primary-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Results tab (default) */}
            {(!result || activeTab === 'results') && (
              <div className="flex-1 overflow-hidden">
                <ErrorBoundary onReset={resetAnalysis}>
                  <ResultsPanel
                    result={result}
                    isLoading={isAnalyzing}
                    mcRealismByStrategy={mcRealismByStrategy}
                    setMcRealismByStrategy={setMcRealismByStrategy}
                    onMonteCarloRealismChange={recomputeMonteCarlo}
                  />
                </ErrorBoundary>
              </div>
            )}

            {/* Cost Stack tab */}
            {result && activeTab === 'cost-stack' && (
              <div className="flex-1 overflow-y-auto bg-white rounded-lg border border-gray-200 p-4 text-sm">
                <h2 className="font-semibold text-gray-800 mb-3">AC Fee Stack — Indicative Breakdown</h2>
                <p className="text-xs text-gray-500 mb-4">
                  Based on service tier <strong>{result.property_snapshot?.service_tier || 'SL2'}</strong> and
                  monthly rent estimate <strong>£{result.property_snapshot?.financials?.monthly_rent?.toLocaleString() || '—'}</strong>.
                  Actual fees depend on tenancy events, capex, and regime.
                </p>
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-1.5 text-gray-500 font-medium">Fee item</th>
                      <th className="text-left py-1.5 text-gray-500 font-medium">Basis</th>
                      <th className="text-right py-1.5 text-gray-500 font-medium">Indicative p.a.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const tier = result.property_snapshot?.service_tier || 'SL2'
                      const rent = result.property_snapshot?.financials?.monthly_rent || 0
                      const annual = rent * 12
                      const hmo = result.property_snapshot?.hmo_flag || false
                      const rows = []

                      if (tier === 'SL1') {
                        rows.push(['Continuing Tenancy Compliance', '6% of annual rent', `£${(annual * 0.06).toLocaleString(undefined, {maximumFractionDigits:0})}`])
                        rows.push(['Introduction fee (yr 1)', '8.4% sole / min £1,200', `£${Math.max(annual * 0.084, 1200).toLocaleString(undefined, {maximumFractionDigits:0})}`])
                      } else if (tier === 'SL2') {
                        rows.push(['Full management', '10.8% of annual rent', `£${(annual * 0.108).toLocaleString(undefined, {maximumFractionDigits:0})}`])
                        rows.push(['Income & expenditure schedule', 'Fixed', '£120'])
                      } else {
                        const pct = hmo ? 16.8 : 14.4
                        rows.push([`Full management${hmo ? ' (incl. HMO supplement)' : ''}`, `${pct}% of annual rent`, `£${(annual * pct / 100).toLocaleString(undefined, {maximumFractionDigits:0})}`])
                        rows.push(['Property visit (1/yr)', 'Fixed', '£120'])
                        rows.push(['Income & expenditure schedule', 'Fixed', '£120'])
                      }

                      rows.push(['Gas safety certificate', 'Annual', '£96'])
                      rows.push(['EICR (amortised over 5yr)', 'Every 5 years', '£43'])
                      rows.push(['Legionella RA (amortised over 2yr)', 'Every 2 years', '£75'])

                      const epc = normalizeEpcBand(result.property_snapshot?.features?.epc_rating)
                      const target = String(result.property_snapshot?.epc_min_rating_target || 'E')
                        .trim()
                        .toUpperCase()
                      if (epc && !['A', 'B', 'C'].includes(epc) && target === 'C') {
                        const capex =
                          epc === 'D' ? 8000 : epc === 'E' ? 12000 : epc === 'F' || epc === 'G' ? 18000 : null
                        if (capex != null) {
                          rows.push([
                            `EPC upgrade to C — ESTIMATE`,
                            'One-off (yr 3 assumed)',
                            `£${capex.toLocaleString()}`,
                          ])
                        }
                      }

                      return rows.map(([name, basis, cost], i) => (
                        <tr key={i} className="border-b border-gray-100 last:border-0">
                          <td className="py-1.5 text-gray-700">{name}</td>
                          <td className="py-1.5 text-gray-400">{basis}</td>
                          <td className="py-1.5 text-right font-medium text-gray-800">{cost}</td>
                        </tr>
                      ))
                    })()}
                  </tbody>
                </table>
                <p className="text-xs text-gray-400 mt-3">
                  Fees per Austin Chambers Landlord Lettings, Management & Compliance Guide v.13.03.2026.
                  EPC upgrade costs are estimates — confirm with a qualified surveyor.
                </p>
              </div>
            )}

            {/* Regulatory Regime tab */}
            {result && activeTab === 'regime' && (
              <div className="flex-1 overflow-y-auto bg-white rounded-lg border border-gray-200 p-4 text-sm space-y-4">
                <h2 className="font-semibold text-gray-800">Regulatory Regime</h2>

                {/* Regime badge */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Active regime:</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    result.property_snapshot?.regulatory_regime === 'POST_RRA'
                      ? 'bg-amber-100 text-amber-800'
                      : result.property_snapshot?.regulatory_regime === 'BOTH'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {result.property_snapshot?.regulatory_regime === 'POST_RRA'
                      ? 'Post Renters\' Rights Act 2026'
                      : result.property_snapshot?.regulatory_regime === 'PRE_RRA'
                      ? 'Pre Renters\' Rights Act'
                      : 'Both regimes (paired)'}
                  </span>
                </div>

                <div className="space-y-3 text-xs text-gray-700">
                  <div className="bg-amber-50 border border-amber-200 rounded p-3">
                    <p className="font-semibold text-amber-800 mb-1">Renters' Rights Act 2026 (from 1 May 2026)</p>
                    <ul className="space-y-1 list-disc pl-4 text-amber-700">
                      <li>Section 21 no-fault evictions abolished — possession only via amended Section 8 grounds</li>
                      <li>All tenancies become periodic by default — no fixed-term renewals</li>
                      <li>Rent reviews limited to once per 12 months; tenant tribunal challenge route</li>
                      <li>Under SL1: renewal fee replaced by 6% Continuing Tenancy Compliance Fee p.a.</li>
                      <li>Turnover probability modelled at 42% p.a. (vs 35% pre-RRA) due to longer possession tail</li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <p className="font-semibold text-blue-800 mb-1">EPC tightening (illustrative 2028 scenario)</p>
                    <p className="text-blue-700">
                      Commentary often cites <strong>EPC C</strong> as a direction of travel for PRS energy standards — this is{' '}
                      <span className="whitespace-nowrap">not legal advice.</span>                       Listed band for this property:{' '}
                      <strong>{formatListedEpc(result.property_snapshot?.features?.epc_rating)}</strong>
                      . Stress-test minimum band used in the fee/capex model for this run:{' '}
                      <strong>
                        {String(result.property_snapshot?.epc_min_rating_target || 'E').trim().toUpperCase() || 'E'}
                      </strong>
                      .
                    </p>
                    {(() => {
                      const band = normalizeEpcBand(result.property_snapshot?.features?.epc_rating)
                      const target = String(result.property_snapshot?.epc_min_rating_target || 'E')
                        .trim()
                        .toUpperCase()
                      return (
                        band &&
                        !['A', 'B', 'C'].includes(band) &&
                        target === 'C' && (
                          <p className="text-amber-700 mt-1 font-medium">
                            ⚠ An indicative EPC upgrade allowance may appear in the Cost Stack tab — ESTIMATE only;
                            confirm with a surveyor.
                          </p>
                        )
                      )
                    })()}
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded p-3">
                    <p className="font-semibold text-gray-700 mb-1">Section 24 (Finance Cost Restriction)</p>
                    <p className="text-gray-600">
                      {result.property_snapshot?.held_in_company
                        ? 'Property held in company name — Section 24 does not apply.'
                        : 'Personal name holding — mortgage interest relief restricted to 20% basic-rate tax credit. Modelled in L2 simulation.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Deal Settings tab */}
            {result && activeTab === 'deal-settings' && (
              <div className="flex-1 overflow-y-auto bg-white rounded-lg border border-gray-200 p-4 text-sm">
                <h2 className="font-semibold text-gray-800 mb-3">Deal Settings</h2>
                <p className="text-xs text-gray-400 mb-4">
                  These parameters were used for this analysis. To change them, re-submit the property with updated settings.
                </p>
                <dl className="space-y-2 text-xs">
                  {[
                    ['Service tier', result.property_snapshot?.service_tier || 'SL2'],
                    ['HMO flag', result.property_snapshot?.hmo_flag ? 'Yes' : 'No'],
                    ['Landlord residency', result.property_snapshot?.landlord_residency === 'NON_RESIDENT' ? 'Non-Resident (NRL)' : 'UK Resident'],
                    ['Held in company', result.property_snapshot?.held_in_company ? 'Yes' : 'No'],
                    ['Regulatory regime', result.property_snapshot?.regulatory_regime || 'POST_RRA'],
                    ['Rent Guarantee Scheme', result.property_snapshot?.rent_guarantee_scheme ? 'Active (3% premium)' : 'Not active'],
                    ['Payout currency', result.property_snapshot?.payout_currency || 'GBP'],
                    ['Tenants', result.property_snapshot?.n_tenants ?? 1],
                    ['Guarantors', result.property_snapshot?.n_guarantors ?? 0],
                    ['Furnished', result.property_snapshot?.furnished !== false ? 'Yes' : 'No'],
                    ['Agency type', result.property_snapshot?.sole_or_multi_agency || 'sole'],
                    ['EPC minimum target', String(result.property_snapshot?.epc_min_rating_target || 'E').trim().toUpperCase() || 'E'],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between border-b border-gray-100 pb-1.5">
                      <dt className="text-gray-500">{label}</dt>
                      <dd className="font-medium text-gray-800">{String(value)}</dd>
                    </div>
                  ))}
                </dl>
                {stlAdvisoryEnabled && (
                  <div className="mt-4 border border-amber-200 rounded p-3 bg-amber-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold text-amber-800">Short-Term Let advisory</p>
                        <p className="text-xs text-amber-700 mt-0.5">
                          Include STL comparator in strategy output (Sia's book only — not shown in landlord report).
                        </p>
                      </div>
                      <button
                        onClick={() => setStlIncluded(v => !v)}
                        className={`ml-4 relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${stlIncluded ? 'bg-amber-500' : 'bg-gray-300'}`}
                        role="switch"
                        aria-checked={stlIncluded}
                      >
                        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${stlIncluded ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    </div>
                    {stlIncluded && (
                      <p className="mt-2 text-xs text-amber-600 italic">
                        STL comparator active — re-run analysis to update MC output.
                      </p>
                    )}
                  </div>
                )}
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-700">
                  Service tier is set by Austin Chambers in consultation with the landlord.
                  SL1/SL2/SL3 selection is a relationship decision — not auto-assigned by the model.
                </div>
              </div>
            )}

            {/* Deal Pack tab */}
            {result && activeTab === 'deal-pack' && (
              <DealPackTab result={result} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnalysisDashboard
